import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NavbarButtonComponent } from '../NavbarButton/navbar-button.component';
import { ProfileCardComponent } from '../profile-card.component';
import { AuthService } from '@services/auth.service';
import { StatsService } from '@services/stats.service';
import { Subscription } from 'rxjs';
import { ResponsiveService } from '@services/responsive.service';
import { PromptService } from '@services/prompt.service';
import { SynchronizationService } from '@services/synchronization.service';
import { ClientProcessingService } from '@services/client-processing.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'mealcheck-informations-band',
  templateUrl: './mealcheck-informations-band.html',
  styleUrls: ['./mealcheck-informations-band.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    NavbarButtonComponent,
    ProfileCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class MealcheckInformationsBandComponent implements OnInit, OnDestroy {
  private responsiveService = inject(ResponsiveService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastrService);
  private synchronizationService = inject(SynchronizationService);
  private clientProcessingService = inject(ClientProcessingService);
  private promptService = inject(PromptService);
  private authService = inject(AuthService);
  private statsService = inject(StatsService);

  isInformationsBandShown: boolean = false;
  isSettingsDropdownShown: boolean = false;
  areSettingsEnabled: boolean = false;

  onPreferencesOpenRequest = output<void>();

  sub = new Subscription();

  protected isCollapsed: WritableSignal<boolean> = signal(false);
  protected communesCount = signal(0);
  protected guestsCount = signal(0);

  #destroyed = false;

  ngOnInit(): void {
    this.sub.add(
      this.authService.user$.subscribe(user => {
        if (!user) {
          this.areSettingsEnabled = false;
          return;
        }

        this.areSettingsEnabled =
          user.roles.includes('ROLE_PORTAGE_ADMIN') ||
          user.roles.includes('ROLE_ADMIN');
      })
    );

    this.sub.add(
      this.statsService.totalEffectif$.subscribe(data => {
        this.#animateCounter(this.communesCount, data.communes);
        this.#animateCounter(this.guestsCount, data.guests);
      })
    );
  }

  ngOnDestroy(): void {
    this.#destroyed = true;
    this.sub.unsubscribe();
  }

  #animateCounter(
    target: WritableSignal<number>,
    value: number,
    duration = 1200
  ): void {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      target.set(value);
      return;
    }

    const from = target();
    const start = performance.now();
    const animate = (now: number) => {
      if (this.#destroyed) return;
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      target.set(Math.round(from + (value - from) * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }

  toggleInformationsBand() {
    this.isInformationsBandShown = !this.isInformationsBandShown;
  }

  handleMobileNavigation() {
    // Ferme le menu uniquement si affiché (mode mobile)
    this.isInformationsBandShown = false;
  }

  @HostListener('window:resize')
  onResize() {
    const { width, height } = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    const isMobile: boolean = this.responsiveService.isMobile();

    if (isMobile) {
      if (this.isCollapsed()) {
        this.isCollapsed.set(false);
      }
    } else {
      if (width > 1300) {
        this.isInformationsBandShown = false;
      } else {
        if (this.isCollapsed()) {
          this.isCollapsed.set(false);
        }
      }
    }
  }

  syncRequested(){
    this.promptService.askForConfirmation(
      'sync.title',
      'sync.message',
      'sync.sync',
      'common.cancel'
    ).then(confirmed => {
      if (confirmed) {
        this.clientProcessingService.setProcessing(true);
        this.sub.add(
          this.synchronizationService.startSynchronization().subscribe({
            next: () => {
              this.toastService.success(
                this.translateService.instant('sync.started')
              );
            },
            error: () => {
              this.clientProcessingService.setProcessing(false);
              this.toastService.error(
                this.translateService.instant('errors.failed_validation')
              );
            },
          })
        );
      }
    });
  }
}
